import { createFileRoute } from '@tanstack/react-router';
import { BotIcon } from 'lucide-react';
import { useEffect } from 'react';
import z from 'zod';

import { useGetMachineQuery } from '@/api/api-hooks';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { EntityNotFound404 } from '@/components/EntityNotFound404';
import { MachineOverview } from '@/components/Machines/MachineOverview';
import { MachineSettings } from '@/components/Machines/MachineSettings';
import { MachineSources } from '@/components/Machines/MachineSources';
import { MachineTeams } from '@/components/Machines/MachineTeams';
import { SheetTabs } from '@/components/SheetTabs/SheetTabs';
import { SheetWithHeader } from '@/components/SheetWithHeader';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';

const tabTypes = ['overview', 'teams', 'sources', 'settings'] as const;
type TabType = (typeof tabTypes)[number];

const machineDetailsParamsSchema = z.object({
  tab: z.enum(tabTypes),
  machineId: z.string(),
  organizationName: z.string(),
});

const assignmentTypeValues = ['direct', 'inherited'] as const;

const machineDetailsSearchSchema = z.object({
  // MachineTeams search params (prefixed with 't')
  tPageSize: z.number().default(DEFAULT_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
  tPageIndex: z.number().default(0).catch(0),
  tSearch: searchParam().optional(),
  // MachineSources search params (prefixed with 's')
  sPageSize: z.number().default(DEFAULT_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
  sPageIndex: z.number().default(0).catch(0),
  sSearch: searchParam().optional(),
  sAssignment: z.enum(assignmentTypeValues).optional(),
});

export const Route = createFileRoute('/$organizationName/settings/machines/$machineId/$tab')({
  component: MachineSheetPage,
  params: { parse: machineDetailsParamsSchema.parse },
  validateSearch: machineDetailsSearchSchema,
});

function MachineSheetPage() {
  const { organizationDisplayName, routeOrganizationName } = useUser();
  const { machineId, tab } = Route.useParams();
  const navigate = Route.useNavigate();

  const {
    data: machine,
    isLoading: isMachineLoading,
    isError,
  } = useGetMachineQuery({ machineId }, { refetchOnWindowFocus: false, retry: false });

  useEffect(() => {
    let section = '';
    switch (tab) {
      case 'overview':
        section = 'Overview';
        break;
      case 'teams':
        section = 'Teams';
        break;
      case 'sources':
        section = 'Sources';
        break;
      case 'settings':
        section = 'Settings';
        break;
    }
    document.title = `${organizationDisplayName || 'Driver'} - ${machine?.name || 'Machine'} - ${section}`;
  }, [organizationDisplayName, machine?.name, tab]);

  const isNotFound = isError || (!isMachineLoading && !machine);

  return (
    <SheetWithHeader
      isOpen
      renderHeaderSkeleton={isMachineLoading}
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            to: '/$organizationName/settings/machines',
            params: { organizationName: routeOrganizationName },
            search: (prev) => ({
              ...prev,
              tPageSize: undefined,
              tPageIndex: undefined,
              tSearch: undefined,
              sPageSize: undefined,
              sPageIndex: undefined,
              sSearch: undefined,
              sAssignment: undefined,
            }),
          });
        }
      }}
      title={machine ? machine.name : !isMachineLoading ? 'Not found' : ''}
      description="Manage machine authorization to Sources"
      icon={<BotIcon className="h-8 w-8" />}
      widthClassName="w-[90vw] !max-w-[1000px]"
    >
      {isNotFound ? (
        <EntityNotFound404 message="Machine not found" />
      ) : (
        <SheetTabs
          value={tab}
          onValueChange={(value) =>
            navigate({
              to: '/$organizationName/settings/machines/$machineId/$tab',
              params: { organizationName: routeOrganizationName, machineId, tab: value as TabType },
              search: (prev) => ({
                ...prev,
                tPageSize: undefined,
                tPageIndex: undefined,
                tSearch: undefined,
                sPageSize: undefined,
                sPageIndex: undefined,
                sSearch: undefined,
                sAssignment: undefined,
              }),
            })
          }
          tabs={[
            {
              value: 'overview',
              label: 'Overview',
              content: <MachineOverview machine={machine} isMachineLoading={isMachineLoading} />,
            },
            {
              value: 'teams',
              label: 'Teams',
              content: <MachineTeams route={Route} machineId={machineId} machineName={machine?.name} />,
            },
            {
              value: 'sources',
              label: 'Sources',
              content: (
                <MachineSources
                  route={Route}
                  machineId={machineId}
                  machineName={machine?.name}
                  orgAccess={machine?.org_access}
                  isMachineLoading={isMachineLoading}
                />
              ),
            },
            {
              value: 'settings',
              label: 'Settings',
              content: <MachineSettings route={Route} machine={machine} isMachineLoading={isMachineLoading} />,
            },
          ]}
        />
      )}
    </SheetWithHeader>
  );
}
